/**
 * What a link turned out to be.
 *
 * Its own module because both the thread and a story's replies show one, and
 * a copy in each would be a copy to keep in step.
 *
 * Everything on it is optional except the address: a page that says nothing
 * about itself still gets a card with its host on it, which is more than the
 * bare URL said. The picture was copied into R2 when the message was written,
 * so it is served from the same gateway as every other image here and the site
 * being linked never learns who looked.
 */
import type { LinkPreview } from '../../shared/types';
import { urlForKey } from './data';
import { Photo } from './Photo';

export function LinkCard({ preview }: { preview: LinkPreview }) {
  const host = hostOf(preview.url);

  return (
    <a
      className="link-card"
      href={preview.url}
      target="_blank"
      rel="noreferrer noopener"
      // The row underneath owns press-and-hold and swipe-to-reply.
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      {preview.imageKey && (
        <Photo
          className="link-card-image"
          src={urlForKey(preview.imageKey)}
          alt=""
          loading="lazy"
          /*
           * A reply's picture is kept under the expiring prefix, so it goes a
           * day or so after the reply itself is due to. Rather than leave a
           * broken frame in the card, the card carries on without it.
           */
          onError={(event) => {
            event.currentTarget.style.display = 'none';
          }}
        />
      )}
      <span className="link-card-body">
        <span className="link-card-site">{preview.siteName || host}</span>
        {preview.title && <span className="link-card-title">{preview.title}</span>}
        {preview.description && <span className="link-card-text">{preview.description}</span>}
      </span>
    </a>
  );
}

/** The host on its own, for the line above the headline. */
function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}
